import { useEffect, useRef, useCallback, useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOCATION_ENABLED_KEY = 'locationTrackingEnabled';

export function useLocationTracker(user) {
  // Initialize from localStorage
  const [isTracking, setIsTracking] = useState(false);
  const [permissionStatus, setPermissionStatus] = useState('prompt');
  const [lastUpdate, setLastUpdate] = useState(null);
  const [isEnabled, setIsEnabled] = useState(() => {
    return localStorage.getItem(LOCATION_ENABLED_KEY) === 'true';
  });
  
  const watchIdRef = useRef(null);
  const intervalRef = useRef(null);
  const lastLocationRef = useRef(null);

  const sendLocation = useCallback(async (position) => {
    if (!user) return;
    if (user.role === 'administrator') return;

    const locationData = {
      latitude: position.coords.latitude,
      longitude: position.coords.longitude,
      accuracy: position.coords.accuracy
    };

    // Don't send if location hasn't changed significantly (more than 10 meters)
    if (lastLocationRef.current) {
      const distance = getDistanceInMeters(
        lastLocationRef.current.latitude,
        lastLocationRef.current.longitude,
        locationData.latitude,
        locationData.longitude
      );
      if (distance < 10) return;
    }

    lastLocationRef.current = locationData;

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/location/update`, locationData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLastUpdate(new Date());
      console.log('Location updated successfully');
    } catch (error) {
      console.error('Error updating location:', error);
    }
  }, [user]);

  const startTracking = useCallback(() => {
    if (!navigator.geolocation) {
      console.log('Geolocation is not supported by this browser');
      setPermissionStatus('denied');
      return;
    }

    if (!user || user.role === 'administrator') {
      return;
    }

    // Save preference to localStorage
    localStorage.setItem(LOCATION_ENABLED_KEY, 'true');
    setIsEnabled(true);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setPermissionStatus('granted');
        setIsTracking(true);
        sendLocation(position);
        
        // Start watching for changes
        watchIdRef.current = navigator.geolocation.watchPosition(
          sendLocation,
          (error) => {
            console.error('Error watching position:', error);
          },
          {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 60000
          }
        );

        // Also send updates every 30 seconds
        intervalRef.current = setInterval(() => {
          navigator.geolocation.getCurrentPosition(sendLocation);
        }, 30000);
      },
      (error) => {
        console.error('Error getting initial position:', error);
        if (error.code === error.PERMISSION_DENIED) {
          setPermissionStatus('denied');
          localStorage.setItem(LOCATION_ENABLED_KEY, 'false');
          setIsEnabled(false);
        }
        setIsTracking(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  }, [user, sendLocation]);

  const stopTracking = useCallback(() => {
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setIsTracking(false);
    // Save preference to localStorage
    localStorage.setItem(LOCATION_ENABLED_KEY, 'false');
    setIsEnabled(false);
  }, []);

  // Check permission status on mount
  useEffect(() => {
    if (navigator.permissions) {
      navigator.permissions.query({ name: 'geolocation' }).then((result) => {
        setPermissionStatus(result.state);
        result.onchange = () => {
          setPermissionStatus(result.state);
        };
      });
    }
  }, []);

  // Auto-start tracking if enabled in localStorage and permission is granted
  useEffect(() => {
    if (user && user.role !== 'administrator') {
      // Check if location was enabled before
      const wasEnabled = localStorage.getItem(LOCATION_ENABLED_KEY) === 'true';
      
      if (wasEnabled && permissionStatus === 'granted') {
        startTracking();
      } else if (wasEnabled && permissionStatus === 'prompt') {
        // Try to start tracking (will prompt for permission)
        startTracking();
      }
    }

    return () => {
      // Don't call stopTracking on cleanup to preserve the enabled state
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [user, permissionStatus, startTracking]);

  return { 
    isTracking, 
    permissionStatus, 
    lastUpdate,
    isEnabled,
    startTracking, 
    stopTracking 
  };
}

// Helper function to calculate distance between two coordinates
function getDistanceInMeters(lat1, lon1, lat2, lon2) {
  const R = 6371e3;
  const φ1 = lat1 * Math.PI / 180;
  const φ2 = lat2 * Math.PI / 180;
  const Δφ = (lat2 - lat1) * Math.PI / 180;
  const Δλ = (lon2 - lon1) * Math.PI / 180;

  const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
            Math.cos(φ1) * Math.cos(φ2) *
            Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
}
